import Analytics from '@opencare/analytics';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { COUNTRY } from 'common/constants';
import { ValidationRulePreset } from 'common/Validator/ValidationRules';
import { createImmutableFormValidatorConfig, FieldValidation, useFormValidator } from 'common/Validator/Validator';

import { USER_SIGNUP_ERROR_TYPES } from 'connect/constants';
import { Signup } from 'connect/saga/handlers/user/signup';
import { selectDataClinicCountry, selectDataUserAuthenticated } from 'connect/selectors';
import { Input, InputType } from 'pages/checkout/components/common/Input';
import { CheckoutLinkButton, FormCheckbox, FormCheckboxWrapper, FormError } from 'pages/checkout/components/common/input-styled-components';
import { Form } from 'pages/checkout/components/common/layout-styled-components';
import { SmsPromoDisclaimer } from 'pages/checkout/components/common/SmsPromoDisclaimer';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { useCheckoutRedesignAnalyticsData } from 'pages/checkout/utils/analyticsUtils';

import { formatPhoneNumberForApi } from 'pages/checkout/utils/phoneUtils';

import Logger from 'utils/Logger';

import { AlternateLoginSection, DividerWithText, FormFieldsWrapper, LoginText, SubmitButton } from '../Info-styled-components';

import { SsoButtons } from './SsoButtons/SsoButtons';

const analytics = new Analytics();

const SIGNUP_FORM_VALIDATOR_CONFIG = createImmutableFormValidatorConfig<void>([
  { field: 'email', preset: ValidationRulePreset.email },
  { field: 'phone', preset: ValidationRulePreset.phone },
  { field: 'phone', preset: ValidationRulePreset.phoneCountryCode },
]);

interface ValidationState {
  allValid?: boolean;
  email?: FieldValidation;
  phone?: FieldValidation;
  phoneCountryCode?: FieldValidation;
}

interface SignupErrorProps {
  shouldShowError: boolean;
  handleLogInClick: () => void;
  error: string | null;
  rawError: string | null;
}

const SignupError = ({ shouldShowError, handleLogInClick, error, rawError }: SignupErrorProps) => {
  if (!shouldShowError || !error) {
    return null;
  }

  if (rawError === USER_SIGNUP_ERROR_TYPES.EMAIL_ALREADY_REGISTERED) {
    if (!handleLogInClick) {
      Logger.error('handleLoginButton is required when rawError is EMAIL_ALREADY_REGISTERED');
      return <FormError>{error}</FormError>;
    }

    return (
      <FormError>
        <span>
          A user with this email already exists.{' '}
          <CheckoutLinkButton onClick={handleLogInClick}>Log in</CheckoutLinkButton>{' '}
          instead.
        </span>
      </FormError>
    );
  }

  return <FormError>{error}</FormError>;
};

export const SignupForm = ({ setLogIn }: { setLogIn: () => void; nextStepHref: string; }) => {
  const { goToNextStep } = useNextCheckoutStep();
  const analyticsData = useCheckoutRedesignAnalyticsData();
  const formValidator = useFormValidator(SIGNUP_FORM_VALIDATOR_CONFIG);

  const dispatch = useDispatch();

  const clinicCountry = useSelector(selectDataClinicCountry);
  const isUserLoggedIn = useSelector(selectDataUserAuthenticated);
  const signupError = useSelector(Signup.error!.selector);
  const prettySignupError = useMemo(() => Signup.getPrettyErrorMessage(signupError), [signupError]);
  const signupLoading = useSelector(Signup.loading!.selector);

  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [promoSmsAllowed, setPromoSmsAllowed] = useState(false);
  const [formValidity, setFormValidity] = useState<ValidationState>({});

  const validationState = useMemo(() => ({
    email,
    phone,
  }), [email, phone]);

  const handleValidate = useCallback(() => {
    const validationResult = formValidator.validate(validationState);
    setFormValidity(validationResult);
    return validationResult;
  }, [validationState, formValidator]);

  // Validate when formState changes
  useEffect(() => {
    if (!validationState) {
      return;
    }

    handleValidate();
  }, [handleValidate, validationState]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Don't refresh
    e.preventDefault();

    const validationResult = handleValidate();

    if (!validationResult.allValid) {
      // Show validation errors after first submission attempt
      setShowValidationErrors(true);
      return;
    }

    const userData = {
      email,
      mobilePhone: formatPhoneNumberForApi(phone),
    } as { email: string; mobilePhone: string; settings?: { promoSmsAllowed: boolean; }; };

    if (typeof promoSmsAllowed === 'boolean') {
      userData.settings = {
        promoSmsAllowed,
      };
    }

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'signup',
      content: {
        email_provided: String(email),
        phone_provided: String(phone),
        promo_sms_allowed: String(promoSmsAllowed),
        ...analyticsData.content,
      },
    });

    setSubmitting(true);
    dispatch(Signup.createAction(userData));
  };

  // Go to next step if we submitted and signup is done loading without error
  useEffect(() => {
    if (submitting && !signupError && !signupLoading && isUserLoggedIn) {
      goToNextStep();
    }
  }, [
    submitting,
    signupError,
    signupLoading,
    isUserLoggedIn,
    goToNextStep,
  ]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    setPhone(newPhone);
  };

  const shouldShowError = Boolean(prettySignupError) && !signupLoading;

  // If form is invalid, show validation errors when clicking the button
  const handleDisabledButtonClick = () => {
    if (formValidity.allValid) {
      return;
    }

    setShowValidationErrors(true);
  };

  const handleLogInClick = () => {
    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'signup',
      content: {
        action: 'login-click',
        ...analyticsData.content,
      },
    });

    setLogIn();
  };

  const handleSmsPromoToggle = () => {
    const newPromoSmsAllowed = !promoSmsAllowed;
    setPromoSmsAllowed(newPromoSmsAllowed);

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'signup',
      content: {
        action: 'sms-promo-toggle',
        sms_promo_allowed: String(newPromoSmsAllowed),
        ...analyticsData.content,
      },
    });
  };

  const emailErrorMessage = useMemo(() => {
    if (!showValidationErrors) {
      return undefined;
    }

    return formValidity.email?.message;
  }, [showValidationErrors, formValidity]);

  const phoneErrorMessage = useMemo(() => {
    if (!showValidationErrors) {
      return undefined;
    }

    const { phone, phoneCountryCode } = formValidity;

    return phone?.message || phoneCountryCode?.message;
  }, [showValidationErrors, formValidity]);

  return (
    <>
      <SignupError
        shouldShowError={shouldShowError}
        rawError={signupError}
        error={prettySignupError}
        handleLogInClick={handleLogInClick}
      />

      <Form onSubmit={handleSubmit}>
        <FormFieldsWrapper>
          <Input
            type={InputType.EMAIL}
            placeholder="Enter your email"
            autoComplete="email"
            name="email"
            value={email}
            onChange={handleEmailChange}
            errorMessage={emailErrorMessage}
          />
          <Input
            type={InputType.TEL}
            placeholder="Enter your phone"
            autoComplete="tel-national"
            name="phone"
            value={phone}
            onChange={handlePhoneChange}
            errorMessage={phoneErrorMessage}
          />
          {clinicCountry === COUNTRY.unitedStates
          && (
            <FormCheckboxWrapper>
              <FormCheckbox
                checked={promoSmsAllowed}
                onChange={handleSmsPromoToggle}
              />
              I agree to receive SMS with promotional content at the phone number provided above.
            </FormCheckboxWrapper>
          )}
        </FormFieldsWrapper>

        <SubmitButton
          type="submit"
          $loading={signupLoading}
          $disabled={!formValidity.allValid}
          onClick={handleDisabledButtonClick}
        >
          Continue Booking
        </SubmitButton>
      </Form>
      <AlternateLoginSection>
        <DividerWithText>or</DividerWithText>
        <SsoButtons />
        <LoginText>
          Already have an account?{' '}
          <CheckoutLinkButton onClick={handleLogInClick}>Log in</CheckoutLinkButton>
        </LoginText>
      </AlternateLoginSection>

      <SmsPromoDisclaimer />
    </>
  );
};
